/**
 * Information-security risk register scoring.
 *
 * THE MATRIX
 * Likelihood and impact are each graded 1–5, and the score is their product,
 * 1–25. This is the classic 5×5 risk matrix every ISO 27001 / NIS2 / DORA
 * assessor expects to see, and the reason the product is STORED rather than
 * computed on read is the same as everywhere else in this codebase: the
 * register sorts and the heatmap buckets on it, and a value two requests
 * compute slightly differently is a value an auditor cannot trust.
 *
 * NOT THE VENDOR SCALE
 * This is deliberately unrelated to the 0–3 composite in `risk.ts`. That scores
 * a supplier; this scores the organisation's own risks. Keeping them apart is
 * what stops a 2.25 vendor score being read as a matrix cell.
 *
 * THE BAND RAMP
 * The four bands reuse the priority/risk-level names (LOW → CRITICAL) on
 * purpose, so a band maps straight onto the existing status-tone ramp
 * (good → critical) without inventing a second colour vocabulary. The
 * boundaries follow the conventional 5×5 banding: the low-left corner is low,
 * the high-right corner is critical, and the diagonal climbs through the middle.
 */
import { RISK_AXIS_MIN, RISK_AXIS_MAX } from './enums.js';
/** Lowest and highest possible product on the 5×5 matrix. */
export const RISK_SCORE_MIN = RISK_AXIS_MIN * RISK_AXIS_MIN; // 1
export const RISK_SCORE_MAX = RISK_AXIS_MAX * RISK_AXIS_MAX; // 25
/** True when a value is an integer on the 1–5 axis. */
export function isRiskAxisValue(value) {
    return Number.isInteger(value) && value >= RISK_AXIS_MIN && value <= RISK_AXIS_MAX;
}
/**
 * The stored inherent/residual score: likelihood × impact.
 *
 * Throws on an off-scale input rather than clamping, because a 7 or a 0 reaching
 * here is a validation bug upstream, and silently scoring it would hide the bug
 * and write a number no cell on the matrix corresponds to.
 */
export function riskScore(likelihood, impact) {
    if (!isRiskAxisValue(likelihood) || !isRiskAxisValue(impact)) {
        throw new RangeError(`risk axis values must be integers ${RISK_AXIS_MIN}–${RISK_AXIS_MAX}, got (${likelihood}, ${impact})`);
    }
    return likelihood * impact;
}
/**
 * The band a 1–25 score falls in, named as a RiskLevel so it lands directly on
 * the shared status-tone ramp (LOW=good … CRITICAL=critical).
 *
 *   1–4   LOW       tolerable; the bottom-left corner of the matrix
 *   5–9   MEDIUM    keep under review
 *   10–14 HIGH      treat
 *   15–25 CRITICAL  treat urgently; the top-right corner
 */
export function riskBand(score) {
    if (score <= 4)
        return 'LOW';
    if (score <= 9)
        return 'MEDIUM';
    if (score <= 14)
        return 'HIGH';
    return 'CRITICAL';
}
/** Band for a likelihood × impact pair, for colouring a heatmap cell. */
export function riskBandFor(likelihood, impact) {
    return riskBand(riskScore(likelihood, impact));
}
//# sourceMappingURL=riskRegister.js.map